import type { ExerciseTemplate } from '@yuan-math/exercises';
import { add, fraction, gcd, lcm, mul, rawFraction, sub, toLatex, toText } from '@yuan-math/math';
import { choiceExercise, fractionExercise, fractionTrap, numericExercise, options, template, trap } from './authoring.ts';

/**
 * Fraction exercises.
 *
 * Every template here carries traps: the specific wrong answers a student
 * produces when they hold a specific wrong idea. `2/5` as the answer to
 * `1/2 + 1/3` is not a random error — it is "adding denominators", and the
 * platform says so by name.
 */
export const FRACTION_TEMPLATES: ExerciseTemplate[] = [
  template({
    id: 'frac-name-shaded',
    conceptId: 'fraction-basics',
    description: 'Name the fraction shown by a shaded bar.',
    difficulties: [1, 2],
    build: ({ rng, difficulty }) => {
      const parts = difficulty === 1 ? rng.int(2, 6) : rng.int(5, 12);
      const shaded = rng.int(1, parts - 1);
      return fractionExercise({
        prompt: 'What fraction of the bar is shaded?',
        figure: { kind: 'fraction-bar', parts, shaded },
        value: rawFraction(shaded, parts),
        requireExactForm: true,
        summary: `The bar is cut into ${parts} equal pieces and ${shaded} are shaded, so the fraction is ${shaded}/${parts}.`,
        hint: 'Count the total number of equal pieces first — that goes on the bottom.',
        steps: [
          { label: 'Count all the equal pieces. That is the denominator.', latex: String(parts) },
          { label: 'Count the shaded pieces. That is the numerator.', latex: String(shaded) },
          { label: 'Write the fraction.', latex: toLatex(rawFraction(shaded, parts)) },
        ],
        traps: [trap(`${parts}/${shaded}`, 'variable-as-label')],
      });
    },
  }),

  template({
    id: 'frac-of-set',
    conceptId: 'fraction-basics',
    description: 'Find a unit fraction of a whole-number quantity.',
    difficulties: [2, 3],
    build: ({ rng, difficulty }) => {
      const denominator = rng.int(2, difficulty === 2 ? 5 : 9);
      const each = rng.int(2, difficulty === 2 ? 8 : 12);
      const total = denominator * each;
      return numericExercise({
        prompt: `There are ${total} counters. What is 1/${denominator} of them?`,
        latex: `\\frac{1}{${denominator}} \\times ${total}`,
        value: each,
        summary: `Finding 1/${denominator} of a group means sharing it into ${denominator} equal parts: ${total} ÷ ${denominator} = ${each}.`,
        hint: `Share ${total} counters equally between ${denominator} groups.`,
        steps: [
          { label: 'A unit fraction of a group means sharing equally.', latex: `${total} \\div ${denominator}` },
          { label: 'Divide.', latex: String(each) },
        ],
        traps: [trap(total * denominator, 'multiplication-always-grows'), trap(total - denominator, 'divide-wrong-direction')],
      });
    },
  }),

  template({
    id: 'frac-equivalent-missing',
    conceptId: 'equivalent-fractions',
    description: 'Complete an equivalent fraction with a missing numerator.',
    difficulties: [1, 2, 3],
    build: ({ rng, difficulty }) => {
      const denominator = rng.int(2, difficulty === 1 ? 5 : 9);
      const numerator = rng.int(1, denominator - 1);
      const factor = rng.int(2, difficulty === 3 ? 8 : 4);
      const target = denominator * factor;
      const answer = numerator * factor;
      return numericExercise({
        prompt: 'Fill in the missing numerator.',
        latex: `\\frac{${numerator}}{${denominator}} = \\frac{?}{${target}}`,
        value: answer,
        summary: `The denominator was multiplied by ${factor}, so the numerator must be too: ${numerator} × ${factor} = ${answer}.`,
        hint: `What do you multiply ${denominator} by to get ${target}?`,
        steps: [
          { label: 'Find what the denominator was multiplied by.', latex: `${denominator} \\times ${factor} = ${target}` },
          { label: 'Do the same to the numerator.', latex: `${numerator} \\times ${factor} = ${answer}` },
          { label: 'The equivalent fraction.', latex: `\\frac{${numerator}}{${denominator}} = \\frac{${answer}}{${target}}` },
        ],
        // Adding the same amount to both parts instead of multiplying.
        traps: [trap(numerator + (target - denominator), 'add-denominators')],
      });
    },
  }),

  template({
    id: 'frac-simplify',
    conceptId: 'simplify-fractions',
    description: 'Write a fraction in lowest terms.',
    difficulties: [2, 3, 4],
    build: ({ rng, difficulty }) => {
      const base = fraction(rng.int(1, 7), rng.int(2, 9));
      const factor = rng.int(2, difficulty >= 3 ? 9 : 4);
      const n = base.n * factor;
      const d = base.d * factor;
      const divisor = gcd(n, d);
      return fractionExercise({
        prompt: 'Write this fraction in its lowest terms.',
        latex: toLatex(rawFraction(n, d)),
        value: fraction(n, d),
        requireSimplified: true,
        summary: `${n} and ${d} are both divisible by ${divisor}, giving ${toText(fraction(n, d))}.`,
        hint: `What is the largest number that divides both ${n} and ${d}?`,
        steps: [
          { label: 'Find the greatest common factor.', latex: `\\gcd(${n}, ${d}) = ${divisor}` },
          { label: 'Divide both parts by it.', latex: `\\frac{${n} \\div ${divisor}}{${d} \\div ${divisor}}` },
          { label: 'Lowest terms.', latex: toLatex(fraction(n, d)) },
        ],
      });
    },
  }),

  template({
    id: 'frac-compare-unit',
    conceptId: 'compare-fractions',
    description: 'Compare two unit fractions — the "bigger denominator" trap.',
    difficulties: [1, 2],
    build: ({ rng }) => {
      const small = rng.int(2, 5);
      const large = small + rng.int(2, 6);
      const { choices, correctId } = options(
        [
          { latex: `\\frac{1}{${small}}`, correct: true },
          { latex: `\\frac{1}{${large}}`, misconceptionId: 'larger-denominator-is-larger' },
        ],
        rng.shuffle,
      );
      return choiceExercise({
        prompt: 'Which fraction is larger?',
        choices,
        correctId,
        summary: `Cutting a whole into ${large} pieces makes smaller pieces than cutting it into ${small}, so 1/${small} is larger.`,
        hint: 'Imagine sharing one cake between that many people. More people means less each.',
        steps: [
          { label: 'Both have one piece, so compare the piece sizes.', latex: `\\frac{1}{${small}} \\text{ vs } \\frac{1}{${large}}` },
          { label: 'More pieces in the whole means each piece is smaller.', latex: `${small} < ${large} \\Rightarrow \\frac{1}{${small}} > \\frac{1}{${large}}` },
        ],
      });
    },
  }),

  template({
    id: 'frac-compare-general',
    conceptId: 'compare-fractions',
    description: 'Compare two fractions with unlike denominators.',
    difficulties: [3, 4],
    build: ({ rng }) => {
      const d1 = rng.int(3, 9);
      const d2 = rng.int(3, 9);
      const n1 = rng.int(1, d1 - 1);
      const n2 = rng.int(1, d2 - 1);
      const a = fraction(n1, d1);
      const b = fraction(n2, d2);
      const aIsLarger = a.n * b.d > b.n * a.d;
      const equal = a.n * b.d === b.n * a.d;
      const { choices, correctId } = options(
        [
          { latex: toLatex(rawFraction(n1, d1)), correct: !equal && aIsLarger },
          { latex: toLatex(rawFraction(n2, d2)), correct: !equal && !aIsLarger },
          { label: 'They are equal', correct: equal },
        ],
        rng.shuffle,
      );
      const common = lcm(d1, d2);
      return choiceExercise({
        prompt: 'Which is larger?',
        choices,
        correctId,
        summary: `Rewritten over ${common}: ${(n1 * common) / d1}/${common} and ${(n2 * common) / d2}/${common}.`,
        hint: 'Rewrite both fractions with the same denominator, then just compare the numerators.',
        steps: [
          { label: 'Find a common denominator.', latex: `\\operatorname{lcm}(${d1}, ${d2}) = ${common}` },
          { label: 'Rewrite both.', latex: `\\frac{${(n1 * common) / d1}}{${common}} \\text{ and } \\frac{${(n2 * common) / d2}}{${common}}` },
          { label: 'Compare the numerators.', latex: `${(n1 * common) / d1} \\text{ vs } ${(n2 * common) / d2}` },
        ],
      });
    },
  }),

  template({
    id: 'frac-lcd',
    conceptId: 'common-denominators',
    description: 'Find the least common denominator of two fractions.',
    difficulties: [2, 3, 4],
    build: ({ rng, difficulty }) => {
      const ceiling = difficulty === 2 ? 6 : difficulty === 3 ? 9 : 12;
      const d1 = rng.int(2, ceiling);
      const d2 = rng.int(2, ceiling);
      const answer = lcm(d1, d2);
      return numericExercise({
        prompt: `What is the least common denominator of these two fractions?`,
        latex: `\\frac{${rng.int(1, d1)}}{${d1}} \\quad \\text{and} \\quad \\frac{${rng.int(1, d2)}}{${d2}}`,
        value: answer,
        summary: `The smallest number that both ${d1} and ${d2} divide into is ${answer}.`,
        hint: `Count up in ${d1}s until you reach a multiple of ${d2}.`,
        steps: [
          { label: `Multiples of ${d1}.`, latex: [1, 2, 3, 4].map((k) => d1 * k).join(', \; ') + ', \\ldots' },
          { label: `Multiples of ${d2}.`, latex: [1, 2, 3, 4].map((k) => d2 * k).join(', \; ') + ', \\ldots' },
          { label: 'The smallest they share.', latex: String(answer) },
        ],
        traps: [trap(d1 + d2, 'add-denominators'), ...(d1 * d2 !== answer ? [trap(d1 * d2, 'cross-multiply-to-add')] : [])],
      });
    },
  }),

  template({
    id: 'frac-add-like',
    conceptId: 'add-fractions',
    description: 'Add two fractions that already share a denominator.',
    difficulties: [1, 2],
    build: ({ rng }) => {
      const d = rng.int(3, 10);
      const n1 = rng.int(1, d - 2);
      const n2 = rng.int(1, d - n1 - 1);
      const answer = fraction(n1 + n2, d);
      return fractionExercise({
        prompt: 'Add. Give your answer in lowest terms.',
        latex: `\\frac{${n1}}{${d}} + \\frac{${n2}}{${d}}`,
        value: answer,
        requireSimplified: true,
        summary: `The pieces are the same size, so count them: ${n1} + ${n2} = ${n1 + n2} of them.`,
        hint: 'The denominators already match, so you only need to add the top numbers.',
        steps: [
          { label: 'Same denominator, so add the numerators.', latex: `\\frac{${n1} + ${n2}}{${d}}` },
          { label: 'Compute.', latex: toLatex(rawFraction(n1 + n2, d)) },
          ...(gcd(n1 + n2, d) > 1 ? [{ label: 'Simplify.', latex: toLatex(answer) }] : []),
        ],
        traps: [fractionTrap(rawFraction(n1 + n2, d + d), 'add-denominators')],
      });
    },
  }),

  template({
    id: 'frac-add-unlike',
    conceptId: 'add-fractions',
    description: 'Add two fractions with different denominators.',
    difficulties: [3, 4, 5],
    build: ({ rng, difficulty }) => {
      const ceiling = difficulty === 3 ? 6 : difficulty === 4 ? 9 : 12;
      const d1 = rng.int(2, ceiling);
      let d2 = rng.int(2, ceiling);
      if (d2 === d1) d2 = (d2 % ceiling) + 1;
      const n1 = rng.int(1, d1 - 1 || 1);
      const n2 = rng.int(1, d2 - 1 || 1);
      const a = rawFraction(n1, d1);
      const b = rawFraction(n2, d2);
      const answer = add(a, b);
      const common = lcm(d1, d2);
      return fractionExercise({
        prompt: 'Add. Give your answer in lowest terms.',
        latex: `${toLatex(a)} + ${toLatex(b)}`,
        value: answer,
        requireSimplified: true,
        summary: `Rewrite both over ${common}, then add the numerators.`,
        hint: 'The pieces are different sizes. Find a common denominator before adding anything.',
        steps: [
          { label: 'Find the least common denominator.', latex: `\\operatorname{lcm}(${d1}, ${d2}) = ${common}` },
          { label: 'Rewrite both fractions.', latex: `\\frac{${(n1 * common) / d1}}{${common}} + \\frac{${(n2 * common) / d2}}{${common}}` },
          { label: 'Add the numerators, keep the denominator.', latex: `\\frac{${(n1 * common) / d1 + (n2 * common) / d2}}{${common}}` },
          { label: 'Simplify.', latex: toLatex(answer) },
        ],
        traps: [
          fractionTrap(rawFraction(n1 + n2, d1 + d2), 'add-denominators'),
          fractionTrap(mul(a, b), 'cross-multiply-to-add'),
        ],
      });
    },
  }),

  template({
    id: 'frac-subtract',
    conceptId: 'add-fractions',
    description: 'Subtract two fractions.',
    difficulties: [3, 4],
    build: ({ rng, difficulty }) => {
      const ceiling = difficulty === 3 ? 8 : 12;
      const d1 = rng.int(2, ceiling);
      let d2 = rng.int(2, ceiling);
      if (d2 === d1) d2 = (d2 % ceiling) + 1;
      const n1 = rng.int(1, d1 - 1 || 1);
      const n2 = rng.int(1, d2 - 1 || 1);
      let a = rawFraction(n1, d1);
      let b = rawFraction(n2, d2);
      // Keep the answer positive: negative numbers are not yet on the map.
      if (a.n * b.d < b.n * a.d) [a, b] = [b, a];
      const answer = sub(a, b);
      const common = lcm(a.d, b.d);
      return fractionExercise({
        prompt: 'Subtract. Give your answer in lowest terms.',
        latex: `${toLatex(a)} - ${toLatex(b)}`,
        value: answer,
        requireSimplified: true,
        summary: `Rewrite both over ${common}, then subtract the numerators.`,
        hint: 'Same rule as addition: make the pieces the same size first.',
        steps: [
          { label: 'Common denominator.', latex: String(common) },
          { label: 'Rewrite.', latex: `\\frac{${(a.n * common) / a.d}}{${common}} - \\frac{${(b.n * common) / b.d}}{${common}}` },
          { label: 'Subtract the numerators.', latex: toLatex(answer) },
        ],
        traps: [fractionTrap(rawFraction(Math.abs(a.n - b.n), Math.abs(a.d - b.d) || 1), 'add-denominators')],
      });
    },
  }),

  template({
    id: 'frac-multiply',
    conceptId: 'multiply-fractions',
    description: 'Multiply two proper fractions.',
    difficulties: [2, 3, 4],
    build: ({ rng, difficulty }) => {
      const ceiling = difficulty === 2 ? 5 : difficulty === 3 ? 8 : 10;
      const d1 = rng.int(2, ceiling);
      const d2 = rng.int(2, ceiling);
      const n1 = rng.int(1, d1 - 1 || 1);
      const n2 = rng.int(1, d2 - 1 || 1);
      const a = rawFraction(n1, d1);
      const b = rawFraction(n2, d2);
      const answer = mul(a, b);
      return fractionExercise({
        prompt: 'Multiply. Give your answer in lowest terms.',
        latex: `${toLatex(a)} \\times ${toLatex(b)}`,
        value: answer,
        requireSimplified: true,
        summary: `Multiply straight across: ${n1} × ${n2} over ${d1} × ${d2}, then simplify.`,
        hint: 'No common denominator needed. Multiply the tops, multiply the bottoms.',
        steps: [
          { label: 'Multiply the numerators and the denominators.', latex: `\\frac{${n1} \\times ${n2}}{${d1} \\times ${d2}}` },
          { label: 'Compute.', latex: toLatex(rawFraction(n1 * n2, d1 * d2)) },
          { label: 'Simplify.', latex: toLatex(answer) },
        ],
        traps: [fractionTrap(add(a, b), 'cross-multiply-to-add')],
      });
    },
  }),

  template({
    id: 'frac-of-quantity',
    conceptId: 'multiply-fractions',
    description: 'Find a fraction of a whole number in context.',
    difficulties: [3, 4],
    build: ({ rng }) => {
      const d = rng.int(2, 6);
      const n = rng.int(1, d - 1 || 1);
      const groups = rng.int(3, 12);
      const total = d * groups;
      const answer = (total / d) * n;
      const items = rng.pick(['marbles', 'stickers', 'pages', 'metres of ribbon', 'minutes']);
      return numericExercise({
        prompt: `A box holds ${total} ${items}. How many is ${n}/${d} of them?`,
        latex: `\\frac{${n}}{${d}} \\times ${total}`,
        value: answer,
        summary: `One ${d}th of ${total} is ${total / d}, so ${n} of them is ${answer}.`,
        hint: `First find 1/${d} of ${total}, then multiply by ${n}.`,
        steps: [
          { label: `Find one ${d}th.`, latex: `${total} \\div ${d} = ${total / d}` },
          { label: `Take ${n} of those.`, latex: `${total / d} \\times ${n} = ${answer}` },
        ],
        traps: [trap(total * n, 'multiplication-always-grows')],
      });
    },
  }),

  template({
    id: 'frac-improper-to-mixed',
    conceptId: 'mixed-numbers',
    description: 'Convert an improper fraction to a mixed number.',
    difficulties: [2, 3],
    build: ({ rng, difficulty }) => {
      const d = rng.int(2, difficulty === 2 ? 6 : 9);
      const whole = rng.int(1, difficulty === 2 ? 4 : 8);
      const remainder = rng.int(1, d - 1 || 1);
      const n = whole * d + remainder;
      return numericExercise({
        prompt: `Convert ${n}/${d} to a mixed number. What is the whole-number part?`,
        latex: `\\frac{${n}}{${d}} = ?\\,\\tfrac{${remainder}}{${d}}`,
        value: whole,
        summary: `${n} ÷ ${d} = ${whole} remainder ${remainder}, so the whole part is ${whole}.`,
        hint: `How many whole groups of ${d} fit inside ${n}?`,
        steps: [
          { label: 'Divide the numerator by the denominator.', latex: `${n} \\div ${d} = ${whole} \\text{ r } ${remainder}` },
          { label: 'The quotient is the whole part.', latex: String(whole) },
          { label: 'The remainder is the new numerator.', latex: `${whole}\\tfrac{${remainder}}{${d}}` },
        ],
        traps: [trap(remainder, 'ignored-remainder')],
      });
    },
  }),

  template({
    id: 'frac-mixed-to-improper',
    conceptId: 'mixed-numbers',
    description: 'Convert a mixed number to an improper fraction.',
    difficulties: [2, 3, 4],
    build: ({ rng, difficulty }) => {
      const d = rng.int(2, difficulty >= 3 ? 9 : 5);
      const whole = rng.int(1, difficulty >= 3 ? 9 : 4);
      const n = rng.int(1, d - 1 || 1);
      const answer = rawFraction(whole * d + n, d);
      return fractionExercise({
        prompt: 'Write this mixed number as an improper fraction.',
        latex: `${whole}\\tfrac{${n}}{${d}}`,
        value: answer,
        requireExactForm: true,
        summary: `${whole} wholes is ${whole * d}/${d}; adding ${n}/${d} gives ${toText(answer)}.`,
        hint: `How many ${d}ths are there in ${whole} whole ones?`,
        steps: [
          { label: 'Convert the whole part.', latex: `${whole} \\times ${d} = ${whole * d}` },
          { label: 'Add the numerator.', latex: `${whole * d} + ${n} = ${whole * d + n}` },
          { label: 'Keep the denominator.', latex: toLatex(answer) },
        ],
        traps: [fractionTrap(rawFraction(whole + n, d), 'add-denominators')],
      });
    },
  }),
];
